import React from "react";
import { AvailableProduct } from "../../types";
import { Button, Modal, TextInput, Td, Th, Tr } from "../ui";

interface ProductModalProps {
  show: boolean;
  onClose: () => void;
  availableProducts: AvailableProduct[];
  productQuantities: Record<number, number>;
  setProductQuantities: React.Dispatch<
    React.SetStateAction<Record<number, number>>
  >;
  productSearch: string;
  setProductSearch: React.Dispatch<React.SetStateAction<string>>;
  onAddProducts: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  show,
  onClose,
  availableProducts,
  productQuantities,
  setProductQuantities,
  productSearch,
  setProductSearch,
  onAddProducts,
}) => {
  const filteredProducts =
    availableProducts?.filter(
      (p) =>
        !productSearch ||
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.code.toLowerCase().includes(productSearch.toLowerCase())
    ) || [];

  const handleClose = () => {
    onClose();
    setProductQuantities({});
    setProductSearch("");
  };

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && handleClose()}
      title="Available Products"
      description="Use this dialog to add products in your form."
      className="max-w-3xl"
      footer={
        <Button variant="outline" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <TextInput
          label="Search"
          type="search"
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          placeholder="Code or name…"
          className="w-56"
        />
        <Button variant="outline" onClick={onAddProducts}>
          Add all
        </Button>
      </div>

      <div className="border-hairline/70 overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <Th className="w-24">Code</Th>
              <Th>Name</Th>
              <Th className="w-24">Size</Th>
              <Th className="w-28">Quantity</Th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product, index) => (
              <Tr key={index}>
                <Td className="font-mono text-xs text-zinc-500">
                  {product.code}
                </Td>
                <Td className="font-medium text-zinc-900">{product.name}</Td>
                <Td>{product.size}</Td>
                <Td>
                  <TextInput
                    type="number"
                    min={0}
                    value={productQuantities[index] || ""}
                    onChange={(e) =>
                      setProductQuantities((prev) => ({
                        ...prev,
                        [index]: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="px-3 py-1.5"
                  />
                </Td>
              </Tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-zinc-500">
        Showing 1 to {filteredProducts.length} of{" "}
        {availableProducts?.length || 0} entries
      </p>
    </Modal>
  );
};

export default ProductModal;
